import {
  Conversation,
  Message,
  PageMessages,
  TypeConversation,
} from "../types/models/conversation";
import { HttpClient } from "./http-client";

/**
 * Messagerie : fils de course et support chauffeur.
 *
 * L'écriture passe par HTTP et non par le hub SignalR — elle profite ainsi de
 * la validation et du traitement d'erreurs communs, et reste possible quand le
 * temps réel est tombé. La diffusion aux autres participants part du serveur
 * une fois le message committé (cf. `use-conversation-hub`).
 */
export class ConversationService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  /**
   * Boîte de réception : fils triés du plus récemment animé au plus ancien.
   * Un gestionnaire y reçoit l'ensemble des fils de la plateforme, y compris
   * ceux qu'il n'a jamais ouverts — c'est sa file de traitement.
   */
  async getMesConversations(signal?: AbortSignal): Promise<Conversation[]> {
    return this.http.get<Conversation[]>("/api/conversations", { signal });
  }

  /** Entête d'un fil : participants, état de lecture, dernier message. */
  async getConversation(
    idConversation: number,
    signal?: AbortSignal,
  ): Promise<Conversation> {
    return this.http.get<Conversation>(`/api/conversations/${idConversation}`, {
      signal,
    });
  }

  /**
   * Page de messages, en ordre chronologique. `avant` remonte le fil ;
   * `depuis` rattrape ce qui a été posté après un message donné — c'est le
   * comblement du trou après une coupure du temps réel, que la reconnexion
   * seule ne fait pas.
   */
  async getMessages(
    idConversation: number,
    options: {
      avant?: number;
      depuis?: number;
      taille?: number;
      signal?: AbortSignal;
    } = {},
  ): Promise<PageMessages> {
    const { avant, depuis, taille, signal } = options;

    return this.http.get<PageMessages>(
      `/api/conversations/${idConversation}/messages`,
      { query: { avant, depuis, taille }, signal },
    );
  }

  /**
   * Ouvre un fil, ou renvoie celui qui existe déjà. Idempotent : c'est le point
   * d'entrée normal, appelé à chaque fois que l'utilisateur ouvre une
   * discussion. La composition du fil est réalignée à chaque appel, ce qui fait
   * entrer un chauffeur affecté après coup dans une discussion commencée.
   */
  async ouvrir(
    type: TypeConversation,
    idCible: number,
    signal?: AbortSignal,
  ): Promise<Conversation> {
    return this.http.post<Conversation>(
      "/api/conversations",
      { type, idCible },
      { signal },
    );
  }

  /** Poste un message. Rejeté si le fil est passé en lecture seule. */
  async poster(
    idConversation: number,
    contenu: string,
    signal?: AbortSignal,
  ): Promise<Message> {
    return this.http.post<Message>(
      `/api/conversations/${idConversation}/messages`,
      { contenu },
      { signal },
    );
  }

  /**
   * Pose le marque-page de lecture. Il ne recule jamais côté serveur : deux
   * onglets ouverts ne peuvent pas se renvoyer des non-lus l'un à l'autre.
   */
  async marquerLu(idConversation: number, jusquA?: string): Promise<void> {
    await this.http.post(`/api/conversations/${idConversation}/lu`, {
      jusquA: jusquA ?? null,
    });
  }
}
